import React, { useState } from 'react';
import { C, F, WHATSAPP_URL, EMAIL } from '../theme';

const initial = { name: '', phone: '', who: 'An elderly parent', location: '', message: '' };

const labelText = { display: 'block', fontSize: 13.5, fontWeight: 800, color: '#3A4842', marginBottom: 7 };
const field = {
  width: '100%', fontFamily: 'inherit', fontSize: 15, padding: '13px 15px',
  border: '1.5px solid #E1DACB', borderRadius: 12, background: '#FCFAF5', color: '#22302B', outline: 'none',
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    // Front-end only. Wire this up to your backend / email service / CRM here.
    // e.g. fetch('/api/leads', { method: 'POST', body: JSON.stringify(form) })
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const reset = () => { setForm(initial); setSubmitted(false); };

  return (
    <div className="cn-fade">
      {/* HERO */}
      <section style={{ background: 'linear-gradient(180deg,#F4EFE4 0%,#FBF7F0 100%)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '84px 24px 52px', textAlign: 'center' }}>
          <p style={{ textTransform: 'uppercase', letterSpacing: '.14em', fontSize: 13, fontWeight: 800, color: C.orange, margin: '0 0 18px' }}>Get started</p>
          <h1 style={{ fontFamily: F.serif, fontWeight: 500, fontSize: 52, lineHeight: 1.06, letterSpacing: '-.015em', color: C.tealDeep, margin: '0 0 20px' }}>Tell us about your loved one</h1>
          <p style={{ fontSize: 19, lineHeight: 1.6, color: C.body, margin: 0 }}>Share a few details and our care team will reach out to match you with the right caregiver. There's no obligation until you're ready.</p>
        </div>
      </section>

      <section style={{ maxWidth: 1120, margin: '0 auto', padding: '56px 24px 96px' }}>
        <div data-cn-contact-grid style={{ display: 'grid', gridTemplateColumns: '1.25fr .95fr', gap: 36, alignItems: 'start' }}>

          {/* FORM */}
          <div style={{ background: '#fff', border: '1px solid #E9E2D4', borderRadius: 24, padding: '40px 40px', boxShadow: '0 30px 56px -42px rgba(21,58,51,.4)' }}>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '40px 10px' }}>
                <div style={{ width: 70, height: 70, borderRadius: '50%', background: C.sageTint, color: C.teal, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, margin: '0 auto 22px' }}>✓</div>
                <h2 style={{ fontFamily: F.serif, fontWeight: 500, fontSize: 30, color: C.tealDeep, margin: '0 0 12px' }}>Thank you — we've got it</h2>
                <p style={{ fontSize: 16.5, lineHeight: 1.6, color: '#54635D', margin: '0 auto 26px', maxWidth: 380 }}>Our care team will be in touch shortly. For anything urgent, message us on WhatsApp and we'll respond right away.</p>
                <button onClick={reset} style={{ background: 'none', border: '1.5px solid #C9CFC9', color: C.teal, fontFamily: 'inherit', fontSize: 15, fontWeight: 800, padding: '13px 26px', borderRadius: 999, cursor: 'pointer' }}>Send another request</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h2 style={{ fontFamily: F.serif, fontWeight: 500, fontSize: 28, color: C.tealDeep, margin: '0 0 24px' }}>Request a caregiver</h2>
                <div data-cn-form-row style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <label>
                    <span style={labelText}>Your name</span>
                    <input className="cn-input" type="text" placeholder="Full name" value={form.name} onChange={update('name')} style={field} required />
                  </label>
                  <label>
                    <span style={labelText}>Phone / WhatsApp</span>
                    <input className="cn-input" type="tel" placeholder="+234 ..." value={form.phone} onChange={update('phone')} style={field} required />
                  </label>
                </div>
                <label style={{ display: 'block', marginBottom: 16 }}>
                  <span style={labelText}>Who needs care?</span>
                  <select className="cn-input" value={form.who} onChange={update('who')} style={field}>
                    <option>An elderly parent</option>
                    <option>A child with special needs</option>
                    <option>Someone else in my family</option>
                  </select>
                </label>
                <label style={{ display: 'block', marginBottom: 16 }}>
                  <span style={labelText}>Location</span>
                  <input className="cn-input" type="text" placeholder="City / area" value={form.location} onChange={update('location')} style={field} />
                </label>
                <label style={{ display: 'block', marginBottom: 24 }}>
                  <span style={labelText}>Tell us a little more</span>
                  <textarea className="cn-input" rows={4} placeholder="What kind of help is needed, and any preferences?" value={form.message} onChange={update('message')} style={{ ...field, resize: 'vertical' }} />
                </label>
                <button className="cn-primary-btn" type="submit" style={{ width: '100%', background: C.orange, color: '#fff', border: 'none', fontFamily: 'inherit', fontSize: 17, fontWeight: 800, padding: 16, borderRadius: 14, cursor: 'pointer', boxShadow: '0 14px 30px -12px rgba(224,124,69,.7)' }}>Send my request</button>
                <p style={{ fontSize: 13, color: '#8A948E', textAlign: 'center', margin: '14px 0 0' }}>We handle your information responsibly and share it only with your matched care team.</p>
              </form>
            )}
          </div>

          {/* CONTACT DETAILS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <a className="cn-contact-card wa" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" style={{ background: '#fff', border: '1px solid #E6EDE7', borderRadius: 18, padding: '22px 24px', display: 'flex', gap: 16, alignItems: 'center' }}>
              <span style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 13, background: '#E7F5EC', color: C.waGreenDark, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>✆</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#8A948E', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>WhatsApp</div>
                <div style={{ fontSize: 16.5, fontWeight: 800, color: C.ink }}>+234 812 577 7959</div>
              </div>
            </a>
            <a className="cn-contact-card mail" href={`mailto:${EMAIL}`} style={{ background: '#fff', border: '1px solid #E6EDE7', borderRadius: 18, padding: '22px 24px', display: 'flex', gap: 16, alignItems: 'center' }}>
              <span style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 13, background: C.sageTint, color: C.teal, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>✉</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#8A948E', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>Email</div>
                <div style={{ fontSize: 16.5, fontWeight: 800, color: C.ink }}>{EMAIL}</div>
              </div>
            </a>
            <div style={{ background: '#fff', border: '1px solid #E6EDE7', borderRadius: 18, padding: '22px 24px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <span style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 13, background: C.sageTint, color: C.teal, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>⌂</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#8A948E', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>Office</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, lineHeight: 1.5 }}>2nd Floor, 42 Montgomery Road,<br />Yaba, Lagos</div>
              </div>
            </div>
            <div style={{ background: C.sage, border: '1px solid #DCE7E0', borderRadius: 18, padding: '22px 24px' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#8A948E', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 12 }}>Opening hours</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, color: '#3A4842', padding: '6px 0', borderBottom: '1px dashed #D2DED6' }}><span style={{ fontWeight: 700 }}>Mon – Fri</span><span>8:00am – 6:00pm</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, color: '#3A4842', padding: '6px 0', borderBottom: '1px dashed #D2DED6' }}><span style={{ fontWeight: 700 }}>Saturday</span><span>9:00am – 3:00pm</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, color: '#3A4842', padding: '6px 0' }}><span style={{ fontWeight: 700 }}>Sunday</span><span style={{ color: '#A0A89F' }}>Closed</span></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
